/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { CMSImage } from '@/features/contentstack/components/CMSImage/CMSImage';
import { BlogMediaContent } from './BlogMediaContent';
import logger from '@/utils/logger';
import type { ContentStackPost } from '@/features/contentstack/interface';
import { CMSIframe } from '@/features/contentstack/components/CMSIframe/CMSIframe';
import { getYouTubeEmbedUrl, isYouTubeUrl } from '@/utils/youtubeUrl';
import Head from 'next/head';
import { getVideoSchema } from '@/features/common/utils/getVideoSchema';

const DEFAULT_VIDEO_POSTER = '/images/video-default-poster-gray.svg';

type Props = {
  content?:
    | ContentStackPost['feature_content']
    | NonNullable<ContentStackPost['post_content']>[number]['copy_media'];
  preload?: 'none' | 'metadata' | 'auto';
  mediaType?: NonNullable<ContentStackPost['feature_content']>['media_type'];
  blogCreatedDate: string | undefined;
  priority?: boolean;
};

export function ImageVideoContent({
  content,
  preload = 'auto',
  mediaType = content?.media_type,
  blogCreatedDate,
  priority,
}: Props) {
  const options = {
    feature_cta: content?.feature_cta,
    feature_text: content?.feature_text,
    direction: content?.direction,
    heading: content?.heading,
    heading_underline: content?.heading_underline,
  };

  if (mediaType === 'Video') {
    const videoUrl = content?.video?.video_url;

    const posterImg = content?.video?.poster_image ?? DEFAULT_VIDEO_POSTER;

    if (!videoUrl) {
      logger.warn({
        message: 'Video is missing for featured content',
      });
      return null;
    }

    const videoSchema = getVideoSchema({
      name: content.heading || 'Video',
      description: content.video?.video_description,
      thumbnailUrl: posterImg,
      contentUrl: isYouTubeUrl(videoUrl) ? undefined : videoUrl,
      uploadDate: blogCreatedDate,
    });

    return (
      <>
        {videoSchema && (
          <Head>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchema) }}
            />
          </Head>
        )}
        {isYouTubeUrl(videoUrl) ? (
          <BlogMediaContent
            options={options}
            mediaElement={
              <CMSIframe
                content={{
                  source_url: {
                    title: content?.heading || 'Youtube Video',
                    href: getYouTubeEmbedUrl(videoUrl),
                  },
                  title: content?.heading || 'YouTube Video',

                  iframe_height: {
                    mobile: 193,
                    tablet: 200,
                    desktop: 295,
                  },
                }}
              />
            }
          />
        ) : (
          <BlogMediaContent
            options={options}
            mediaElement={
              <video
                preload={preload}
                controls
                src={videoUrl}
                width="100%"
                controlsList="nodownload"
                poster={posterImg}
              />
            }
          />
        )}
      </>
    );
  }

  if (!content?.image?.image_url) {
    logger.warn({
      message: 'Image is missing for featured content',
    });
    return null;
  }

  return (
    <BlogMediaContent
      options={options}
      mediaElement={
        <CMSImage
          image={content.image}
          priority={priority}
          loading={priority ? 'eager' : undefined}
        />
      }
    />
  );
}
